import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  ListItemText,
  Button,
  IconButton,
  Autocomplete,
  TextField
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ConfirmFormModal, DrawStage } from "@toorak/tc-common-fe-sdk";
import { useDrawsStyles } from "../Draws.styles";
import {
  deleteBankDetailsById,
  updateDrawDetails
} from "../../../stores/drawRequest/drawRequest.action";
import { RootState } from "../../../stores/rootReducer";
import { isOrigUser } from "../../../utils/AccessManagement";
import {
  typeCast,
  typeCastOnBlur
} from "../../../create-loan/tape-to-file/TapeToFileUtils";
import { DrawRequestStore } from "../../../stores/drawRequest/drawRequest.interface";
import { DrawViewModeTextField } from "../DrawViewModeTextField";
import { useStyles } from "../../AssetManagement.styles";
import deleteImage from "../../../images/Delete.svg";
import { AMNotEditingBankStages } from "../Constant";
import {
  updateSelectedBankObj,
  updateVisibleRouting,
  updateDrawAvalBank
} from "../../../stores/drawRequest/drawRequest.reducer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { decryptBankDetailField } from "../../../network/apiService";
import { USStates } from "../../../create-loan/constants/loanCreationDropDownValues";
import {
  BANK_DEFAULT_COUNTRY,
  BANK_ZIPCODE_LENGTH
} from "../../../stores/drawRequest/drawRequest.constants";

type RevealableField = "accountNumber" | "routing" | "accountName";

const MASKED_ACCOUNT_X_COUNT = 8;
const MASKED_ROUTING_X_COUNT = 9;
const MASKED_ACCOUNT_NAME_X_MAX = 20;

const sanitizeZipCode = (value: any): string =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, BANK_ZIPCODE_LENGTH);


// Country is fixed to USA and rendered read-only, so it is always pre-filled.
const emptyBankAddress = () => ({
  city: "",
  state: "",
  zipcode: "",
  country: BANK_DEFAULT_COUNTRY
});

interface StateAutocompleteProps {
  value: any;
  onChange: (code: string) => void;
  disabled?: boolean;
  testId: string;
  width?: string;
  hasError?: boolean;
  /** Matches the trailing gap of whichever input class sits beside it. */
  marginBottom?: string;
}

/**
 * Type-to-search state picker over the codes in `USStates`.
 *
 * A stored value that is not in the list (an Excel import, or a code retired from
 * `USStates`) is added as an option so it stays selected instead of rendering
 * blank and being overwritten on the next save.
 */
export const StateAutocomplete = ({
  value,
  onChange,
  disabled,
  testId,
  width = "85%",
  hasError = false,
  marginBottom = "0px"
}: StateAutocompleteProps) => {
  const current = value ? String(value) : "";

  return (
    <Autocomplete
      options={USStates}
      value={current || null}
      disabled={disabled}
      autoHighlight
      autoComplete
      openOnFocus
      data-testid={testId}
      onChange={(_event, newValue) => onChange(newValue ?? "")}
      sx={{
        width,
        marginBottom,
        "& .MuiOutlinedInput-root": {
          height: "36px",
          padding: "0 8px 0 0",
          background: "#ffffff",
          fontSize: "12px",
          borderRadius: "4px",
          border: `solid 1px ${hasError ? "red" : "#cad1d7"}`,
          boxShadow: "none !important"
        },
        // The border lives on the root above. MUI recolours and thickens this
        // fieldset on focus and hover, which the plain inputs beside this one
        // never do, so it is taken out of the picture entirely.
        "& .MuiOutlinedInput-notchedOutline": {
          display: "none"
        },
        "& .MuiAutocomplete-input": {
          padding: "2px 5px !important",
          color: "#000000"
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Select state"
          inputProps={{
            ...params.inputProps,
            "data-testid": `${testId}-input`
          }}
        />
      )}
    />
  );
};

// Server masking format differs per field: account number uses "X" + last 4;
// routing/accountName may be stored as last-4 only (digits/chars) or legacy
// full-star masks from GET display. Plaintext routing is 9 digits; plaintext
// account names longer than 4 chars never look like last-4 storage.
const routingLooksMasked = (value: string): boolean =>
  value.includes("*") || /^\d{4}$/.test(value);

const accountNumberLooksMasked = (value: string): boolean =>
  value.includes("X") || value.replace(/\D/g, "").length <= 4;

const looksMaskedFor = (
  field: RevealableField,
  value: string,
  allValues?: Partial<Record<RevealableField, string | number>>,
): boolean => {
  if (field === "accountNumber") return accountNumberLooksMasked(value);
  if (field === "routing") return routingLooksMasked(value);
  const routingMasked = routingLooksMasked(String(allValues?.routing ?? ""));
  const accountNumberMasked = accountNumberLooksMasked(String(allValues?.accountNumber ?? ""));
  return (
    value.includes("*")
    || (value.length > 0 && value.length <= 4 && (routingMasked || accountNumberMasked))
  );
};

/** Applies TC AM display masking for view-mode — API returns stored last-4 only. */
const applyDisplayMask = (value: string, field: RevealableField): string => {
  if (!value) return value;

  if (field === "accountNumber") {
    if (value.includes("X")) return value;
    const digits = value.replace(/\D/g, "");
    if (digits.length > 0 && digits.length <= 4) {
      return "X".repeat(MASKED_ACCOUNT_X_COUNT) + digits.slice(-4);
    }
    return value;
  }

  if (field === "routing") {
    if (value.includes("*") || /^\d{4}$/.test(value)) {
      return "X".repeat(MASKED_ROUTING_X_COUNT);
    }
    return value;
  }

  if (value.includes("*")) {
    return "X".repeat(Math.min(value.length, MASKED_ACCOUNT_NAME_X_MAX));
  }
  if (value.length > 0 && value.length <= 4) {
    return "X".repeat(Math.min(value.length, MASKED_ACCOUNT_NAME_X_MAX));
  }
  return value;
};

export const BankDetails = (props: any) => {
  const styles = useStyles();
  const { selectedLoanId, isSubmitClicked, drawObj, fieldWidth, isEditing } =
    props;
  const dispatch = useDispatch();
  const classes = useDrawsStyles();
  const [bankDetailObj, setBankDetailObj] = useState<any>({});
  const [availableAccountIds, setAvailableAccountIds] = useState<any[]>([]);
  const [openFormModal, setOpenFormModel] = useState(false);
  const [showWaringMgs, setShowWaringMgs] = useState(false);
  const [showRoutingWarningMgs, setShowRoutingWarningMgs] = useState(false);
  const [showZipcodeWarningMgs, setShowZipcodeWarningMgs] = useState(false);
  // Per-field visibility toggle for the masked inputs in the "Add New Bank Account"
  // form. Defaults to masked; the eye icon flips the input between password and text.
  const [showAddAccountNumber, setShowAddAccountNumber] = useState(false);
  const [showAddRouting, setShowAddRouting] = useState(false);
  // Per-field reveal state. Each field is independently toggled — clicking the eye
  // icon on one field never reveals the others. Decrypted plaintext is dropped from
  // state on hide. Cache is intentionally not invalidated on remote updates: bank
  // details change rarely; user can hide+reveal to refresh.
  const [revealed, setRevealed] = useState<Record<RevealableField, boolean>>({
    accountNumber: false,
    routing: false,
    accountName: false
  });
  const [decryptedValues, setDecryptedValues] = useState<
    Partial<Record<RevealableField, string>>
  >({});
  // Stable masked snapshot used as the *base* for the view-mode display. It is
  // captured only from server-masked values, so the decrypted plaintext that we
  // write into bankDetailObj/redux during edit mode (needed so Save can send the
  // real values) can never leak into the view as an "auto-revealed" value. The
  // eye toggle (revealed/decryptedValues) layers on top of this base on demand.
  const [maskedView, setMaskedView] = useState<
    Partial<Record<RevealableField, string>>
  >({});
  // Disables the eye icon while a decrypt request is in flight — prevents a
  // double-click from firing two concurrent decrypts for the same field.
  const [loadingField, setLoadingField] = useState<Record<RevealableField, boolean>>({
    accountNumber: false,
    routing: false,
    accountName: false
  });
  // Tracks whether we're currently auto-decrypting fields for edit mode.
  // Prevents duplicate decrypt calls and allows showing a loading indicator.
  const [decryptingForEdit, setDecryptingForEdit] = useState(false);
  // Ref to track the bankDetailId we already decrypted for, to avoid re-fetching
  // on every render while in edit mode.
  const didDecryptForEditRef = useRef<string>("");
  // LOCAL decrypted plaintext for the edit inputs. This is the source of truth for
  // what the user sees/edits in edit mode. We deliberately do NOT write decrypted
  // plaintext into Redux selectedBankDetails — the autosave watches that object and
  // would send plaintext, which the backend re-encrypts (minting new encryptionIds)
  // on every pass, causing an infinite autosave loop and overriding the user's
  // typing. Keeping plaintext local means Redux stays masked/stable; plaintext only
  // reaches Redux (and the save payload) when the user actually edits a field.
  const [editDecrypted, setEditDecrypted] = useState<
    Partial<Record<RevealableField, string>>
  >({});
  // State for decrypted dropdown accounts: keyed by bankDetailId, value is object with decrypted fields.
  const [decryptedAccounts, setDecryptedAccounts] = useState<
    Record<string, { accountNumber?: string; routing?: string; accountName?: string }>
  >({});
  // Tracks whether we're currently decrypting all dropdown accounts.
  const [decryptingDropdown, setDecryptingDropdown] = useState(false);
  // Ref to track which bankDetailIds we already decrypted for the dropdown.
  const decryptedAccountIdsRef = useRef<Set<string>>(new Set());
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const isOrig = isOrigUser();
  const { drawId } = useParams<any>() as any;
  const [addBankObj, setAddBankObj] = useState<any>(() => ({
    accountNumber: "",
    reAccountNumber: "",
    routing: "",
    reRouting: "",
    bankName: "",
    accountName: "",
    bankDetailId: "",
    ...emptyBankAddress()
  }));
  const didSyncBankDetailIdRef = useRef<string>("");
  const isDraft = drawObj.stage === DrawStage.draft;
  const {
    selectedBankDetails,
    availableBankAccountDetails,
    fieldsHistory,
    drawDetails
  } = useSelector<RootState, DrawRequestStore>(
    (state) => state.drawRequestStore
  );

  useEffect(() => {
    setBankDetailObj(selectedBankDetails);
    // eslint-disable-next-line
  }, [selectedBankDetails]);

  useEffect(() => {
    if (!availableBankAccountDetails) return;

    setAvailableAccountIds(() => availableBankAccountDetails);
  }, [availableBankAccountDetails]);

  useEffect(() => {
    setSelectedAccountNumber("");
    // Clear dropdown decrypt cache when loan changes.
    setDecryptedAccounts({});
    decryptedAccountIdsRef.current = new Set();
    // Drop the masked snapshot and any reveals so a previous loan's bank values
    // can't bleed into the newly selected loan.
    setMaskedView({});
    setRevealed({ accountNumber: false, routing: false, accountName: false });
    setDecryptedValues({});
    // Reset the edit-mode decrypt gate so the newly selected loan's bank is
    // auto-decrypted again. Without this, selecting a primary loan id whose bank
    // shares (or reuses) a previously-seen bankDetailId leaves the masked values
    // in the edit inputs (the ref still reads "already decrypted"). A loan change
    // is the right trigger: it never fires on save or a view↔edit toggle, so the
    // post-save "values go back to masked" and "auto-unmask on edit" flows are
    // unaffected.
    didDecryptForEditRef.current = "";
    // Drop the local decrypted edit cache so the newly selected loan's bank decrypts
    // fresh and a previous bank's plaintext can't linger in the inputs.
    setEditDecrypted({});
    // eslint-disable-next-line
  }, [selectedLoanId]);

  // Reset all per-field reveals (and drop cached plaintext) when wire verification
  // status flips — keeps reveals tied to a single verification cycle.
  useEffect(() => {
    setRevealed({ accountNumber: false, routing: false, accountName: false });
    setDecryptedValues({});
  }, [drawDetails?.wireVerificationStatus]);

  // Clear decryption cache when server returns masked values (e.g., after save).
  // This ensures we show correct masked values and don't display stale decrypted data.
  // Server masking pattern: "X" for accountNumber, "*" for routing/accountName.
  useEffect(() => {
    const accountNum = String(selectedBankDetails?.accountNumber ?? "");
    const routing = String(selectedBankDetails?.routing ?? "");
    const accountName = String(selectedBankDetails?.accountName ?? "");
    const hasServerMasking =
      looksMaskedFor("accountNumber", accountNum, selectedBankDetails)
      || looksMaskedFor("routing", routing, selectedBankDetails)
      || looksMaskedFor("accountName", accountName, selectedBankDetails);

    if (hasServerMasking) {
      // Server sent masked values - clear all decryption cache to show masked values
      setRevealed({ accountNumber: false, routing: false, accountName: false });
      setDecryptedValues({});
      // NOTE: Do NOT clear didDecryptForEditRef here - it prevents auto-decrypt from re-running
      decryptedAccountIdsRef.current = new Set();
    }
  }, [selectedBankDetails?.accountNumber, selectedBankDetails?.routing, selectedBankDetails?.accountName]);

  // Capture the server-provided masked values into `maskedView`. We snapshot a
  // field ONLY when it currently looks masked, so the plaintext we write into
  // selectedBankDetails during edit-mode auto-decrypt is ignored here and never
  // becomes the view-mode base. After a save the server returns fresh masked
  // values, which refresh this snapshot — so the next eye reveal shows the
  // updated value.
  // Fields whose value is now plaintext (e.g. after switching to a freshly-added
  // bank) are explicitly removed so getMaskedDisplay falls back to the live value
  // instead of returning a stale snapshot from the previous bank.
  useEffect(() => {
    const fields: RevealableField[] = ["accountNumber", "routing", "accountName"];
    setMaskedView((prev) => {
      const updated = { ...prev };
      let changed = false;
      fields.forEach((field) => {
        const str = String(selectedBankDetails?.[field] ?? "");
        if (str && looksMaskedFor(field, str, selectedBankDetails)) {
          if (updated[field] !== str) { updated[field] = str; changed = true; }
        } else if (field in updated) {
          delete updated[field]; changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [selectedBankDetails?.accountNumber, selectedBankDetails?.routing, selectedBankDetails?.accountName]);

  // Auto-decrypt sensitive fields when entering edit mode so user sees real values.
  // Runs once per bankDetailId when showingEditBankDetails() becomes true.
  useEffect(() => {
    const inEditMode = showingEditBankDetails();
    const bankDetailId = bankDetailObj?.bankDetailId;

    // Clear decrypt ref and cached values when exiting edit mode so we show
    // fresh masked values from backend (draw.bankDetails) after save.
    if (!inEditMode) {
      didDecryptForEditRef.current = "";
      setDecryptedValues({});
      setRevealed({ accountNumber: false, routing: false, accountName: false });
      // Drop the local plaintext so view mode (and post-save) shows masked values.
      setEditDecrypted({});
      return;
    }

    // NOTE: we intentionally do NOT bail out just because the values currently
    // look masked. On entering edit mode the values are ALWAYS masked (that's
    // what the GET returns), so the old "skip if masked" guard silently blocked
    // the decrypt-on-edit. The post-save re-decrypt it tried to prevent is now
    // handled purely by didDecryptForEditRef below: after a save we keep the ref
    // set (same bankDetailId) so we skip, and on a genuine fresh entry the ref is
    // empty so we decrypt. The view can never leak plaintext regardless, because
    // the view reads the masked snapshot (maskedView), not bankDetailObj.

    // Skip if nothing to decrypt or already decrypted this draw.
    // Use 'inline' as tracking key for orphan draws (no bankDetailId).
    const decryptKey = bankDetailId || 'inline';
    const canDecrypt = canDecryptBankDetails();

    if (!canDecrypt || didDecryptForEditRef.current === decryptKey) {
      return;
    }
    // Freshly-added bank has no real server ID — its values are already
    // plaintext in bankDetailObj, so no decrypt call is needed or possible.
    if (!bankDetailId) {
      return;
    }

    const decryptAllFields = async () => {
      setDecryptingForEdit(true);
      didDecryptForEditRef.current = decryptKey;

      const fields: RevealableField[] = ["accountNumber", "routing", "accountName"];
      const results: Partial<Record<RevealableField, string>> = {};

      // Decrypt all 3 fields in parallel for performance.
      await Promise.all(
        fields.map(async (field) => {
          try {
            const encryptionId = getFieldEncryptionId(field);
            const drawId = drawDetails?.drawId;
            
            const res: any = await decryptBankDetailField(
              String(bankDetailId || 'inline'),
              field,
              isOrig,
              encryptionId,
              drawId
            );
            const value = res?.data?.value ?? res?.value;
            if (value != null) {
              results[field] = value;
            }
          } catch (err) {
            // On error, fall back to masked value (already in bankDetailObj).
            // Global error handler will show toast if needed.
          }
        })
      );

      // Write decrypted plaintext into the LOCAL edit cache only — never into Redux.
      // The edit inputs read from this cache, so masked autosave/load re-dispatches
      // to selectedBankDetails can't override what the user sees, and the autosave
      // is never triggered by the decrypt itself. We do NOT overwrite a field that
      // is already in the cache (a prior decrypt or, more importantly, a value the
      // user has since typed), so a re-run can't clobber an in-progress edit.
      if (Object.keys(results).length > 0) {
        setEditDecrypted((prev) => {
          const merged = { ...prev };
          for (const field of fields) {
            if (results[field] != null && merged[field] == null) {
              merged[field] = results[field];
            }
          }
          return merged;
        });

        // IMPORTANT: do NOT touch `revealed` / `decryptedValues` here. Those drive
        // the view-mode eye toggle only.
      }

      setDecryptingForEdit(false);
    };

    decryptAllFields();
    // eslint-disable-next-line
  }, [
    isEditing,
    isDraft,
    bankDetailObj?.bankDetailId,
    bankDetailObj?.accountNumber,
    bankDetailObj?.accountNumberEncryptionId,
    bankDetailObj?.routingEncryptionId,
    bankDetailObj?.accountNameEncryptionId,
    bankDetailObj?.needsDrawIdDecrypt,
    drawDetails?.stage,
    drawDetails?.drawId
  ]);

  // Publish the routing value the user is actually looking at in the edit input
  // (decrypted plaintext or freshly typed) to Redux, so the header's Save/Submit
  // validation checks that — not the masked last-4 a previous draw's bank can carry
  // in selectedBankDetails.routing. We expose ONLY editDecrypted.routing: when it's
  // absent the input still shows the stored (masked) value, whose real routing is a
  // valid 9-digit number, so we keep this empty and let the header's presence check
  // (not the 9-digit rule) govern. Cleared when not in edit mode.
  useEffect(() => {
    const visible = showingEditBankDetails()
      ? String(editDecrypted.routing ?? "")
      : "";
    dispatch(updateVisibleRouting(visible));
    // eslint-disable-next-line
  }, [
    editDecrypted.routing,
    isEditing,
    isDraft,
    bankDetailObj?.routing,
    bankDetailObj?.bankDetailId,
    drawDetails?.stage
  ]);

  // Auto-decrypt all dropdown accounts when entering edit mode.
  // This allows users to see real values in the dropdown, not masked ones.
  useEffect(() => {
    const inEditMode = isBankedited();
    if (!inEditMode || !availableAccountIds?.length) return;

    // Find accounts with bankDetailId that haven't been decrypted yet.
    const accountsToDecrypt = availableAccountIds.filter(
      (acc) => acc.bankDetailId && !decryptedAccountIdsRef.current.has(String(acc.bankDetailId))
    );

    if (accountsToDecrypt.length === 0) return;

    const decryptDropdownAccounts = async () => {
      setDecryptingDropdown(true);

      const fields: RevealableField[] = ["accountNumber", "routing", "accountName"];
      const results: Record<string, { accountNumber?: string; routing?: string; accountName?: string }> = {};

      // Process accounts with concurrency limit of 5 to avoid overwhelming server.
      const CONCURRENCY_LIMIT = 5;
      for (let i = 0; i < accountsToDecrypt.length; i += CONCURRENCY_LIMIT) {
        const batch = accountsToDecrypt.slice(i, i + CONCURRENCY_LIMIT);

        await Promise.all(
          batch.map(async (account) => {
            const bankDetailId = String(account.bankDetailId);
            results[bankDetailId] = {};

            // Decrypt all 3 fields for this account in parallel.
            const fieldResults = await Promise.allSettled(
              fields.map(async (field) => {
                const res: any = await decryptBankDetailField(bankDetailId, field, isOrig);
                const value = res?.data?.value ?? res?.value;
                return { field, value };
              })
            );

            // Collect successful decrypts.
            for (const result of fieldResults) {
              if (result.status === "fulfilled" && result.value.value != null) {
                results[bankDetailId][result.value.field as RevealableField] = result.value.value;
              }
            }

            // Mark this ID as decrypted (even if some fields failed).
            decryptedAccountIdsRef.current.add(bankDetailId);
          })
        );
      }

      // Merge new results with existing decrypted accounts.
      setDecryptedAccounts((prev) => ({ ...prev, ...results }));
      setDecryptingDropdown(false);
    };

    decryptDropdownAccounts();
    // eslint-disable-next-line
  }, [
    isEditing,
    isDraft,
    availableAccountIds?.length,
    drawDetails?.stage
  ]);

  // Sync Account List dropdown with current bank: preselect only when form data matches a list
  // item; otherwise keep blank (e.g. after user edits any field to something else).
  useEffect(() => {
    if (!availableAccountIds?.length) {
      setSelectedAccountNumber("");
      return;
    }
    const hasCurrentBank =
      bankDetailObj?.accountNumber != null &&
      String(bankDetailObj.accountNumber).trim() !== "" &&
      (bankDetailObj?.routing != null || bankDetailObj?.accountName != null);
    if (!hasCurrentBank) {
      setSelectedAccountNumber("");
      return;
    }
    const match = availableAccountIds.find((acc: any) => {
      if (bankDetailObj?.bankDetailId && acc.bankDetailId) {
        return String(acc.bankDetailId) === String(bankDetailObj.bankDetailId);
      }
      return (
        String(acc.accountNumber || "") === String(bankDetailObj?.accountNumber || "") &&
        String(acc.accountName || "") === String(bankDetailObj?.accountName || "") &&
        String(acc.routing || "") === String(bankDetailObj?.routing || "") &&
        String(acc.bankName || "") === String(bankDetailObj?.bankName || "")
      );
    });
    if (match) {
      setSelectedAccountNumber(String(match.accountNumber));
    }
    // eslint-disable-next-line
  }, [
    availableAccountIds?.length,
    bankDetailObj?.accountNumber,
    bankDetailObj?.accountName,
    bankDetailObj?.routing,
    bankDetailObj?.bankName,
    bankDetailObj?.bankDetailId,
  ]);

  const isBankedited = () => {
    if (isDraft) {
      return true;
    }
    if (isOrig) {
      return isEditing;
    }
    return isEditing && !AMNotEditingBankStages.includes(drawDetails.stage);
  };

  // Helper to get decrypted value for dropdown display.
  // Returns decrypted value if available, otherwise returns original (masked) value.
  const getDropdownFieldValue = (
    item: any,
    field: "accountNumber" | "routing" | "accountName"
  ): string => {
    const bankDetailId = item?.bankDetailId;
    if (bankDetailId && decryptedAccounts[bankDetailId]?.[field] != null) {
      return decryptedAccounts[bankDetailId][field] as string;
    }
    const value = item?.[field] ?? "";
    // Freshly-added bank (no server ID yet) carries plaintext — skip masking.
    if (!bankDetailId) return String(value);
    return applyDisplayMask(String(value), field);
  };

  const showingEditBankDetails = () => {
    const hasBankInfo =
      bankDetailObj &&
      (bankDetailObj.accountNumber ||
        bankDetailObj.routing ||
        bankDetailObj.bankName ||
        bankDetailObj.accountName);

    if ((isEditing || isDraft) && hasBankInfo) {
      if (isOrig) {
        return true;
      }
      return !AMNotEditingBankStages.includes(drawDetails.stage);
    }

    return false;
  };

  // Pure check only — do not call setState/dispatch here (was causing render loop).
  const checkIfBankDetailIdExists = () => {
    if (bankDetailObj?.bankDetailId) {
      return showingEditBankDetails() && !!bankDetailObj.bankDetailId;
    }
    const bankDetails = availableAccountIds.find(
      (bankDetail) =>
        bankDetail.accountNumber === bankDetailObj?.accountNumber &&
        bankDetail.accountName === bankDetailObj?.accountName
    );
    return !!(showingEditBankDetails() && bankDetails?.bankDetailId);
  };

  // When we find a matching bank by account/name but bankDetailId isn't set yet, sync it once.
  useEffect(() => {
    if (bankDetailObj?.bankDetailId) {
      didSyncBankDetailIdRef.current = "";
      return;
    }
    if (!showingEditBankDetails()) return;
    const bankDetails = availableAccountIds.find(
      (bankDetail) =>
        bankDetail.accountNumber === bankDetailObj?.accountNumber &&
        bankDetail.accountName === bankDetailObj?.accountName
    );
    if (!bankDetails?.bankDetailId) return;
    const syncKey = `${bankDetailObj?.accountNumber}|${bankDetailObj?.accountName}`;
    if (didSyncBankDetailIdRef.current === syncKey) return;
    didSyncBankDetailIdRef.current = syncKey;
    handleEditBankDetails(bankDetails.bankDetailId, "bankDetailId");
    // eslint-disable-next-line
  }, [
    bankDetailObj?.accountNumber,
    bankDetailObj?.accountName,
    availableAccountIds?.length,
    bankDetailObj?.bankDetailId
  ]);

  const SENSITIVE_FIELDS: RevealableField[] = [
    "accountNumber",
    "routing",
    "accountName"
  ];

  const handleEditBankDetails = (val: any, key: string, isBlur = false) => {
    let formatterVal = val;
    if (key === "routing") {
      formatterVal = isBlur
        ? typeCastOnBlur(val, "abaroutingnumber")
        : typeCast(val, "abaroutingnumber");
    } else if (key === "accountNumber") {
      formatterVal = String(val ?? "").replace(/\D/g, ""); // numeric-only, keep leading zeros
    } else if (key === "zipcode") {
      formatterVal = sanitizeZipCode(val);
    }

    // Keep the local decrypted cache in sync for sensitive fields so the input keeps
    // showing what the user typed even after a masked autosave response re-dispatches.
    let nextEditDecrypted = editDecrypted;
    if (SENSITIVE_FIELDS.includes(key as RevealableField)) {
      nextEditDecrypted = { ...editDecrypted, [key]: formatterVal };
      setEditDecrypted(nextEditDecrypted);
    }

    const updatedBankDetailObj = JSON.parse(JSON.stringify(bankDetailObj));
    // Carry ALL decrypted sensitive fields (plus this edit) into the Redux payload.
    // Editing one field must not leave the others masked — a mixed plaintext+masked
    // payload makes the backend treat the whole record as masked and drop the edit.
    SENSITIVE_FIELDS.forEach((f) => {
      if (nextEditDecrypted[f] != null) updatedBankDetailObj[f] = nextEditDecrypted[f];
    });
    updatedBankDetailObj[key] = formatterVal;
    setBankDetailObj(updatedBankDetailObj);
    dispatch(updateSelectedBankObj(updatedBankDetailObj));
  };

  const emptyBankObj = {
    accountNumber: "",
    routing: "",
    bankName: "",
    accountName: "",
    bankDetailId: "",
    ...emptyBankAddress()
  };

  const handleBankDetailChange = (accountNo: any) => {
    setSelectedAccountNumber(accountNo ?? "");
    // A different account was chosen — drop the previous account's decrypted cache
    // and reset the decrypt gate so the new account auto-decrypts fresh.
    setEditDecrypted({});
    didDecryptForEditRef.current = "";
    if (accountNo == null || accountNo === "") {
      dispatch(updateSelectedBankObj(emptyBankObj));
      return;
    }
    const tempBankIdsList = availableBankAccountDetails || availableAccountIds;
    const selectedAccount: any = tempBankIdsList.find(
      (key: any) => String(key.accountNumber) === String(accountNo)
    );
    if (selectedAccount) {
      dispatch(updateSelectedBankObj({ ...selectedAccount }));
    }
  };

  const handleAddNewOpen = () => {
    setAddBankObj({
      accountNumber: "",
      reAccountNumber: "",
      routing: "",
      reRouting: "",
      bankName: "",
      accountName: "",
      bankDetailId: "",
      ...emptyBankAddress()
    });
    setShowWaringMgs(false);
    setShowRoutingWarningMgs(false);
    setShowZipcodeWarningMgs(false);
    setShowAddAccountNumber(false);
    setShowAddRouting(false);
    setOpenFormModel(true);
  };

  const handleDeleteBank = async () => {
    await dispatch(deleteBankDetailsById(bankDetailObj.bankDetailId, drawObj));
  };

  const handleAddBankDetailChange = (val: any, key: string, isBlur = false) => {
    const updatedBankDetailObj = JSON.parse(JSON.stringify(addBankObj));
    let formatterVal = val;
    if (key === "routing" || key === "reRouting") {
      formatterVal = isBlur
        ? typeCastOnBlur(val, "abaroutingnumber")
        : typeCast(val, "abaroutingnumber");
    } else if (key === "accountNumber" || key === "reAccountNumber") {
      formatterVal = String(val ?? "").replace(/\D/g, ""); // numeric-only, keep leading zeros
    } else if (key === "zipcode") {
      formatterVal = sanitizeZipCode(val);
      setShowZipcodeWarningMgs(
        formatterVal.length > 0 && formatterVal.length !== BANK_ZIPCODE_LENGTH
      );
    }
    updatedBankDetailObj[key] = formatterVal;
    setAddBankObj(updatedBankDetailObj);
  };

  const validateAccountNumber = (val: any) => {
    if (
      addBankObj.accountNumber &&
      addBankObj.reAccountNumber &&
      addBankObj.reAccountNumber !== addBankObj.accountNumber
    ) {
      setShowWaringMgs(true);
    } else {
      setShowWaringMgs(false);
    }
  };

  const validateRouting = (_val: any) => {
    if (
      addBankObj.routing &&
      addBankObj.reRouting &&
      addBankObj.reRouting !== addBankObj.routing
    ) {
      setShowRoutingWarningMgs(true);
    } else {
      setShowRoutingWarningMgs(false);
    }
  };

  const bankFormBody = () => (
    <Grid item container xs={12} style={{ width: "760px", maxWidth: "100%" }}>
      <Grid
        item
        container
        xs={12}
        style={{
          padding: "0px 20px",
          justifyContent: "space-between",
          alignItems: "flex-start"
        }}
      >
        <div className={classes.bankOption} data-testid="account-number-input">
          <p className={classes.headerText}>
            Account Number<span className={`${classes.asterick}`}>*</span>
          </p>
          <div style={{ position: "relative", marginBottom: "20px" }}>
            <input
              value={addBankObj.accountNumber}
              className={classes.BankInputValue}
              type={showAddAccountNumber ? "text" : "password"}
              inputMode="numeric"
              style={{ paddingRight: "36px", marginBottom: 0 }}
              onChange={(e: any) =>
                handleAddBankDetailChange(e.target.value, "accountNumber")
              }
              onBlur={(e: any) => validateAccountNumber(e.target.value)}
            />
            <IconButton
              size="small"
              onClick={() => setShowAddAccountNumber((prev) => !prev)}
              data-testid="toggle-add-accountNumber-visibility"
              title={
                showAddAccountNumber
                  ? "Hide account number"
                  : "View account number"
              }
              style={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 2
              }}
            >
              {showAddAccountNumber ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </div>
        </div>
        <div
          className={classes.bankOption}
          data-testid="reenter-account-number-input"
        >
          <p className={classes.headerText}>
            Re-Enter Account Number
            <span className={`${classes.asterick}`}>*</span>
          </p>
          <input
            value={addBankObj.reAccountNumber}
            className={`${
              showWaringMgs
                ? classes.BankInputWithError
                : classes.BankInputValue
            }`}
            inputMode="numeric"
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "reAccountNumber")
            }
            onBlur={(e: any) => validateAccountNumber(e.target.value)}
          />
          {showWaringMgs && (
            <span className={classes.errorMgs}>
              Entered account numbers don't match
            </span>
          )}
        </div>
        <div className={classes.bankOption} data-testid="routing-input">
          <p className={classes.headerText}>
            Routing<span className={`${classes.asterick}`}>*</span>
          </p>
          <div style={{ position: "relative", marginBottom: "20px" }}>
            <input
              value={addBankObj.routing}
              type={showAddRouting ? "text" : "password"}
              className={classes.BankInputValue}
              style={{ paddingRight: "36px", marginBottom: 0 }}
              onChange={(e: any) =>
                handleAddBankDetailChange(e.target.value, "routing")
              }
              onBlur={(e: any) => {
                handleAddBankDetailChange(e.target.value, "routing", true);
                validateRouting(e.target.value);
              }}
            />
            <IconButton
              size="small"
              onClick={() => setShowAddRouting((prev) => !prev)}
              data-testid="toggle-add-routing-visibility"
              title={showAddRouting ? "Hide routing" : "View routing"}
              style={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 2
              }}
            >
              {showAddRouting ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </div>
        </div>
        <div
          className={classes.bankOption}
          data-testid="reenter-routing-input"
        >
          <p className={classes.headerText}>
            Re-Enter Routing
            <span className={`${classes.asterick}`}>*</span>
          </p>
          <input
            value={addBankObj.reRouting}
            className={`${
              showRoutingWarningMgs
                ? classes.BankInputWithError
                : classes.BankInputValue
            }`}
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "reRouting")
            }
            onBlur={(e: any) => {
              handleAddBankDetailChange(e.target.value, "reRouting", true);
              validateRouting(e.target.value);
            }}
          />
          {showRoutingWarningMgs && (
            <span className={classes.errorMgs}>
              Entered routing numbers don't match
            </span>
          )}
        </div>
        <div className={classes.bankOption} data-testid="bank-name-input">
          <p className={classes.headerText}>
            Bank Name<span className={`${classes.asterick}`}>*</span>
          </p>
          <input
            value={addBankObj.bankName}
            className={classes.BankInputValue}
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "bankName")
            }
          />
        </div>
        <div className={classes.bankOption} data-testid="account-name-input">
          <p className={classes.headerText}>
            Account Name<span className={`${classes.asterick}`}>*</span>
          </p>
          <input
            value={addBankObj.accountName}
            className={classes.BankInputValue}
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "accountName")
            }
          />
        </div>
        <div className={classes.bankOption} data-testid="city-input">
          <p className={classes.headerText}>
            Beneficiary City<span className={`${classes.asterick}`}>*</span>
          </p>
          <input
            value={addBankObj.city}
            className={classes.BankInputValue}
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "city")
            }
          />
        </div>
        <div className={classes.bankOption} data-testid="state-input">
          <p className={classes.headerText}>
            Beneficiary State<span className={`${classes.asterick}`}>*</span>
          </p>
          <StateAutocomplete
            value={addBankObj.state}
            testId="add-state-select"
            width="100%"
            marginBottom="20px"
            onChange={(code) => handleAddBankDetailChange(code, "state")}
          />
        </div>
        <div className={classes.bankOption} data-testid="zipcode-input">
          <p className={classes.headerText}>Beneficiary Zip Code</p>
          <input
            value={addBankObj.zipcode}
            className={`${
              showZipcodeWarningMgs
                ? classes.BankInputWithError
                : classes.BankInputValue
            }`}
            inputMode="numeric"
            maxLength={BANK_ZIPCODE_LENGTH}
            onChange={(e: any) =>
              handleAddBankDetailChange(e.target.value, "zipcode")
            }
          />
          {showZipcodeWarningMgs && (
            <span className={classes.errorMgs}>
              Zip Code must be {BANK_ZIPCODE_LENGTH} digits
            </span>
          )}
        </div>
        <div className={classes.bankOption} data-testid="country-input">
          <p className={classes.headerText}>Beneficiary Country</p>
          <input
            value={BANK_DEFAULT_COUNTRY}
            className={classes.BankInputValue}
            data-testid="add-country-input"
            readOnly
            disabled
          />
        </div>
      </Grid>
    </Grid>
  );

  const isDisabled = () =>
    !(
      addBankObj.accountNumber &&
      addBankObj.reAccountNumber &&
      addBankObj.routing &&
      addBankObj.reRouting &&
      addBankObj.bankName &&
      addBankObj.accountName &&
      addBankObj.city &&
      addBankObj.state &&
      !showZipcodeWarningMgs &&
      addBankObj.accountNumber === addBankObj.reAccountNumber &&
      addBankObj.routing === addBankObj.reRouting
    );

  const updateSelectedBankDetails = () => {
    const tempObj = {
      accountNumber: addBankObj.accountNumber,
      routing: addBankObj.routing,
      bankName: addBankObj.bankName,
      accountName: addBankObj.accountName,
      bankDetailId: addBankObj.bankDetailId,
      city: addBankObj.city,
      state: addBankObj.state,
      zipcode: addBankObj.zipcode,
      country: addBankObj.country || BANK_DEFAULT_COUNTRY
    };
    // Clear stale caches so the newly added bank's values display correctly
    // without waiting for a page refresh.
    setMaskedView({});
    setRevealed({ accountNumber: false, routing: false, accountName: false });
    setDecryptedValues({});
    // Clear the edit-mode decrypted cache so inputs fall back to bankDetailObj
    // (the new tempObj values) instead of showing the previous bank's plaintext.
    setEditDecrypted({});
    // Reset the decrypt gate so the bank auto-decrypts once it gets a real ID
    // from the server (post-save). The guard below prevents a spurious 'inline'
    // decrypt attempt for the freshly-added bank that has no server ID yet.
    didDecryptForEditRef.current = "";
    dispatch(updateSelectedBankObj(tempObj));
    // Add the new bank to the available list immediately so it appears in the
    // dropdown right away. We also update local availableAccountIds directly so
    // the Select has the new item in the same render cycle as selectedAccountNumber,
    // avoiding a blank-selection flash. For non-draft draws the post-save
    // bank-list refresh replaces this entry with the server-masked version; for
    // draft the autosave eventually persists it and the list is corrected on
    // next page load.
    const existingList = Array.isArray(availableBankAccountDetails) ? availableBankAccountDetails : [];
    const alreadyInList = existingList.some(
      (b: any) =>
        String(b.accountNumber) === String(tempObj.accountNumber) &&
        String(b.routing) === String(tempObj.routing)
    );
    const newList = alreadyInList ? existingList : [...existingList, tempObj];
    if (!alreadyInList) {
      setAvailableAccountIds(newList);
      dispatch(updateDrawAvalBank({ bankDetails: newList, bankList: newList }));
    }
    // Auto-select the newly added bank in the dropdown.
    setSelectedAccountNumber(String(tempObj.accountNumber));
    // Draft draws are handled by the autosave in DrawLoanInformation (1.1 s debounce).
    // Non-draft draws have no autosave, so trigger an explicit PUT here.
    if (!isDraft && drawDetails) {
      dispatch(
        updateDrawDetails(
          drawDetails,
          tempObj,
          drawId,
          isOrig,
          drawDetails.stage,
          drawDetails.originatorId
        )
      );
    }
  };

  const showErrorMsg = (field: any) => {
    const returnJSX = (
      <div className={classes.errorMgs} data-testid="field-error-text">
        This is a mandatory field.
      </div>
    );
    switch (field) {
      case "accountNumber": {
        if (!bankDetailObj?.accountNumber) return returnJSX;
        return null;
      }
      case "routing": {
        if (!bankDetailObj?.routing) return returnJSX;
        return null;
      }
      case "bankName": {
        if (!bankDetailObj?.bankName) return returnJSX;
        return null;
      }
      case "accountName": {
        if (!bankDetailObj?.accountName) return returnJSX;
        return null;
      }
      case "city": {
        if (!bankDetailObj?.city) return returnJSX;
        return null;
      }
      case "state": {
        if (!bankDetailObj?.state) return returnJSX;
        return null;
      }
      case "zipcode": {
        // Optional: blank raises nothing. Only a partially entered value is flagged.
        const zip = String(bankDetailObj?.zipcode ?? "");
        if (!zip) return null;
        if (zip.length !== BANK_ZIPCODE_LENGTH) {
          return (
            <div className={classes.errorMgs} data-testid="field-error-text">
              Zip Code must be {BANK_ZIPCODE_LENGTH} digits.
            </div>
          );
        }
        return null;
      }
    }
  };

  // When a field has been revealed via the eye icon, show the freshly-decrypted
  // plaintext from the backend. Otherwise apply display masking to stored last-4 values.
  const formatMaskedValue = (value: string, field: RevealableField): string =>
    applyDisplayMask(value, field);

  // The masked base for the view. Prefer the server-captured masked snapshot so
  // we never echo the plaintext that lives in bankDetailObj during/after editing.
  // Fall back to bankDetailObj only for accounts that were never masked by the
  // server (e.g. a freshly "Add New"-ed account not yet saved), where showing the
  // user's own entered value is the correct behaviour.
  const getMaskedDisplay = (field: RevealableField): string => {
    const snapshot = maskedView[field];
    if (snapshot != null && snapshot !== "") {
      return formatMaskedValue(snapshot, field);
    }
    const rawValue = bankDetailObj?.[field];
    if (!rawValue) return "";
    return formatMaskedValue(String(rawValue), field);
  };

  const getDisplayValue = (field: RevealableField) => {
    if (revealed[field] && decryptedValues[field] != null) {
      return decryptedValues[field] as string;
    }
    return getMaskedDisplay(field);
  };

  // Check if decryption is possible for the current bank details.
  // Returns true if we have either:
  //   1. bankDetailId (can lookup encryptionIds from bank_details_history)
  //   2. Inline encryptionIds (orphan draws with encrypted data stored in draw.bankDetails)
  //   3. isEncrypted flag (indicates encrypted data exists somewhere)
  //   4. needsDrawIdDecrypt flag AND drawId (orphan without encryption, uses drawId fallback)
  // Only show eye icon if there's actual data to decrypt (accountNumber exists)
  const canDecryptBankDetails = (field?: RevealableField) => {
    const bankDetailId = bankDetailObj?.bankDetailId;
    const hasInlineIds = !!(
      bankDetailObj?.accountNumberEncryptionId ||
      bankDetailObj?.routingEncryptionId ||
      bankDetailObj?.accountNameEncryptionId
    );
    const isEncrypted = bankDetailObj?.isEncrypted === true;
    const needsDrawIdDecrypt = bankDetailObj?.needsDrawIdDecrypt === true;
    const hasDrawId = !!drawDetails?.drawId;

    // Can decrypt via: bankDetailId lookup, inline encryptionIds, or drawId fallback for orphans
    const hasDecryptMeta = !!(bankDetailId || hasInlineIds || isEncrypted || (needsDrawIdDecrypt && hasDrawId));
    if (!hasDecryptMeta) return false;

    // Only show the eye when the specific field actually has a value to reveal — an
    // empty/edge field (e.g. a blanked account number) must not suppress the others.
    if (field) return !!bankDetailObj?.[field];
    return !!(bankDetailObj?.accountNumber || bankDetailObj?.routing || bankDetailObj?.accountName);
  };

  // Get the encryptionId for a specific field from the bankDetailObj (for orphan draws)
  const getFieldEncryptionId = (field: RevealableField): string | undefined => {
    const keyMap: Record<RevealableField, string> = {
      accountNumber: 'accountNumberEncryptionId',
      routing: 'routingEncryptionId',
      accountName: 'accountNameEncryptionId'
    };
    return bankDetailObj?.[keyMap[field]];
  };

  // Check if a history entry value can be decrypted
  const canDecryptHistoryValue = (item: any, valueType: "preValue" | "value"): boolean => {
    // If not encrypted, no need for eye icon
    if (!item?.isEncrypted) return false;

    // The history row carries the real encryptionId for each side (valueEncryptionId /
    // preValueEncryptionId) — only show the eye when one is actually present, otherwise
    // a pre-backfill row would render an eye that can never decrypt.
    const hasEncryptionId = valueType === "preValue"
      ? !!item?.preValueEncryptionId
      : !!item?.valueEncryptionId;
    if (!hasEncryptionId) return false;

    // The backend's `preValue` is unreliable for encrypted bank-detail rows (it ships
    // stale/garbage plaintext), so we never trust it: the presence of a
    // preValueEncryptionId alone gates the eye, and the real previous value is fetched
    // on demand via decryptHistoryValue (which decrypts using preValueEncryptionId).
    if (valueType === "preValue") return true;

    // For the edited value, only offer decrypt when it's actually masked.
    const value = item?.value;
    const field = (item?.key || "accountNumber") as RevealableField;
    return !!value && (
      value.includes("*")
      || /X{4,}/.test(value)
      || looksMaskedFor(field, String(value))
    );
  };

  // The backend's `value` / `preValue` for encrypted bank-detail history rows are
  // unreliable (they can ship stale or plaintext data — see AM-5541), so the popup must
  // NEVER display them directly. For every encrypted row we replace BOTH sides with a
  // static, field-appropriate masked placeholder (derived only from the key, never from
  // the backend string) and reveal the real value on demand via the eye icon, which
  // decrypts using valueEncryptionId / preValueEncryptionId. A side with no encryptionId
  // simply stays masked with no eye (nothing to decrypt — and no plaintext can leak).
  // Non-encrypted rows (isEncrypted=false — e.g. bankDetailId / pre-migration history)
  // pass through untouched.
  const HISTORY_MASK_BY_KEY: Record<string, string> = {
    accountNumber: "XXXXXXXX",
    routing: "*********",
    accountName: "******"
  };
  const historyMaskForKey = (key: string) => HISTORY_MASK_BY_KEY[key] ?? "******";
  const maskEncryptedHistoryValues = (list: any[]) =>
    (list || []).map((item: any) =>
      item?.isEncrypted
        ? {
            ...item,
            value: historyMaskForKey(item?.key),
            preValue: historyMaskForKey(item?.key)
          }
        : item
    );

  // Decrypt a history entry value
  const decryptHistoryValue = async (item: any, valueType: "preValue" | "value"): Promise<string> => {
    // The edit-history row carries the real encryption-service ID for each side:
    //   - valueEncryptionId    → the "Edited Value"
    //   - preValueEncryptionId → the "Previous Value"
    // The PK (editHistoryId) is NOT an encryptionId — sending it makes the backend
    // call the encryption-service with a bogus ID, which 500s.
    const encryptionId = valueType === "preValue"
      ? item?.preValueEncryptionId?.toString()
      : item?.valueEncryptionId?.toString();
    if (!encryptionId) throw new Error("No encryptionId available");

    const response = await decryptBankDetailField(
      "history",
      item?.key || "accountNumber",
      isOrig,
      encryptionId,
      drawDetails?.drawId?.toString()
    );
    return (response as any)?.data?.value || "";
  };

  const toggleReveal = async (field: RevealableField) => {
    if (loadingField[field]) return; // ignore re-clicks while a decrypt is in flight
    if (revealed[field]) {
      // Hide — drop the cached plaintext so it isn't retained in memory.
      setRevealed((r) => ({ ...r, [field]: false }));
      setDecryptedValues((v) => {
        const next = { ...v };
        delete next[field];
        return next;
      });
      return;
    }
    // Reveal — fetch only if we haven't already.
    if (decryptedValues[field] == null) {
      const bankDetailId = bankDetailObj?.bankDetailId;
      const encryptionId = getFieldEncryptionId(field);
      const drawId = drawDetails?.drawId;

      // Need either bankDetailId, encryptionId, or drawId to decrypt
      if (!bankDetailId && !encryptionId && !drawId) return;

      setLoadingField((l) => ({ ...l, [field]: true }));
      try {
        const res: any = await decryptBankDetailField(
          String(bankDetailId || 'inline'), // Use 'inline' as sentinel when no bankDetailId
          field,
          isOrig,
          encryptionId,  // Pass inline encryptionId for orphan draws
          drawId         // Pass drawId as fallback lookup method
        );
        const value = res?.data?.value ?? res?.value;
        setDecryptedValues((v) => ({ ...v, [field]: value }));
      } catch (err: any) {
        // Non-2xx response (network error, server error, invalid field, etc.).
        // The app's global axios error handler already surfaces a user-facing
        // error modal — no extra alert from here. Just exit early so we don't
        // flip `revealed` to true on a failed fetch.
        return;
      } finally {
        setLoadingField((l) => ({ ...l, [field]: false }));
      }
    }
    setRevealed((r) => ({ ...r, [field]: true }));
  };

  return (
    <Grid item container xs={12}>
      {isBankedited() ? (
        <Grid item container xs={12}>
          <Grid
            item
            container
            xs={8}
            className={classes.fieldRow}
            data-testid="account-list-orig"
          >
            <p
              className={`${classes.headerLoanID} ${
                bankDetailObj?.accountNumber === "" ||
                !(availableAccountIds?.length > 0)
                  ? classes.disabledHeaderText
                  : classes.headerLoanID
              }`}
            >
              Account List
            </p>
            <div>
              <FormControl
                variant="outlined"
                className={classes.accountFormControl}
              >
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    }
                    // getContentAnchorEl: null
                  }}
                  value={selectedAccountNumber}
                  labelId="select-accounts"
                  id="demo-simple-select-outlined"
                  data-testid="select-accounts-orig"
                  className={classes.selectRoot}
                  displayEmpty
                  renderValue={(v) =>
                    v === ""
                      ? " "
                      : (() => {
                          const item = availableAccountIds?.find(
                            (i: any) => String(i.accountNumber) === String(v)
                          );
                          if (!item) return v;
                          const accNum = getDropdownFieldValue(item, "accountNumber");
                          const routing = getDropdownFieldValue(item, "routing");
                          const accName = getDropdownFieldValue(item, "accountName");
                          return `${accNum} — ${routing}, ${item.bankName}, ${accName}`;
                        })()
                  }
                  disabled={
                    selectedLoanId === "" || !(availableAccountIds?.length > 0)
                  }
                  onChange={(e) => handleBankDetailChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{decryptingDropdown ? "Loading accounts..." : "Select account"}</em>
                  </MenuItem>
                  {availableAccountIds?.length > 0 &&
                    availableAccountIds.map((item: any) => (
                      <MenuItem
                        key={item.accountNumber}
                        value={item.accountNumber}
                        style={{ display: "block" }}
                      >
                        <ListItemText className={classes.accountNumber}>
                          {getDropdownFieldValue(item, "accountNumber")}
                        </ListItemText>
                        <ListItemText className={classes.otherInfo}>
                          {getDropdownFieldValue(item, "routing")}, {item.bankName}, {getDropdownFieldValue(item, "accountName")}
                        </ListItemText>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <span className={classes.addOption}>
              {checkIfBankDetailIdExists() ? (
                <IconButton
                  onClick={handleDeleteBank}
                  size="large"
                  style={{ transform: `scale(1.3)` }}
                  data-testid="bank-details-delete"
                >
                  <img
                    className={styles.deleteIcon}
                    src={deleteImage}
                    alt="Delete icon"
                  />
                </IconButton>
              ) : null}
              </span>
              <span className={classes.addOption}>
                <span className={classes.or}>OR</span>
                <Button
                  variant="outlined"
                  disabled={selectedLoanId === ""}
                  className={classes.addnewbtn}
                  onClick={handleAddNewOpen}
                  data-testid="bank-details-add-new"
                >
                  Add New
                </Button>
              </span>
            </div>
          </Grid>
        </Grid>
      ) : null}
      <Grid item container xs={12}>
        {/* Global show/hide toggle removed — each sensitive field now has its
            own eye icon so reveals are independent and individually audited. */}
        <Grid
          item
          container
          xs={fieldWidth}
          className={classes.fieldRow}
          data-testid="account-number-view"
        >
          <DrawViewModeTextField
            label="Account Number"
            value={getDisplayValue("accountNumber")}
            editedData={maskEncryptedHistoryValues(fieldsHistory.accountNumber)}
            isEditMode={showingEditBankDetails()}
            enableDecrypt={true}
            canDecrypt={canDecryptHistoryValue}
            onDecrypt={decryptHistoryValue}
            valueEndAdornment={
              !showingEditBankDetails() && canDecryptBankDetails("accountNumber") ? (
                <IconButton
                  size="small"
                  disabled={loadingField.accountNumber}
                  onClick={() => toggleReveal("accountNumber")}
                  data-testid="toggle-accountNumber-visibility"
                  title={revealed.accountNumber ? "Hide account number" : "View account number"}
                  style={{ padding: 2 }}
                >
                  {revealed.accountNumber ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null
            }
          />
          {showingEditBankDetails() ? (
            <input
              value={editDecrypted.accountNumber ?? bankDetailObj?.accountNumber ?? ""}
              placeholder={decryptingForEdit ? "Loading..." : "Enter account number"}
              data-testid="toorak-draw-amount"
              className={classes.inputValue}
              inputMode="numeric"
              disabled={decryptingForEdit}
              style={decryptingForEdit ? { opacity: 0.6 } : undefined}
              onChange={(e: any) =>
                handleEditBankDetails(e.target.value, "accountNumber")
              }
            />
          ) : null}
          {isSubmitClicked ? showErrorMsg("accountNumber") : null}
        </Grid>
        <Grid
          item
          container
          xs={fieldWidth}
          className={classes.fieldRow}
          data-testid="routing-view"
        >
          <DrawViewModeTextField
            label="Routing"
            value={getDisplayValue("routing")}
            editedData={maskEncryptedHistoryValues(fieldsHistory.routing)}
            isEditMode={showingEditBankDetails()}
            enableDecrypt={true}
            canDecrypt={canDecryptHistoryValue}
            onDecrypt={decryptHistoryValue}
            valueEndAdornment={
              !showingEditBankDetails() && canDecryptBankDetails("routing") ? (
                <IconButton
                  size="small"
                  disabled={loadingField.routing}
                  onClick={() => toggleReveal("routing")}
                  data-testid="toggle-routing-visibility"
                  title={revealed.routing ? "Hide routing" : "View routing"}
                  style={{ padding: 2 }}
                >
                  {revealed.routing ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null
            }
          />

          {showingEditBankDetails() ? (
            <input
              value={editDecrypted.routing ?? bankDetailObj?.routing ?? ""}
              placeholder={decryptingForEdit ? "Loading..." : "Enter routing"}
              data-testid="toorak-draw-amount"
              className={classes.inputValue}
              disabled={decryptingForEdit}
              style={decryptingForEdit ? { opacity: 0.6 } : undefined}
              onChange={(e: any) =>
                handleEditBankDetails(e.target.value, "routing")
              }
              onBlur={(e: any) =>
                handleEditBankDetails(e.target.value, "routing", true)
              }
            />
          ) : null}
          {isSubmitClicked ? showErrorMsg("routing") : null}
        </Grid>
        <Grid
          item
          container
          xs={fieldWidth}
          className={classes.fieldRow}
          data-testid="bank-name-view"
        >
          <DrawViewModeTextField
            label="Bank Name"
            value={bankDetailObj?.bankName}
            editedData={fieldsHistory.bankName}
            isEditMode={!!showingEditBankDetails()}
          />

          {showingEditBankDetails() ? (
            <input
              value={bankDetailObj?.bankName}
              placeholder={decryptingForEdit ? "Loading..." : "Enter bank name"}
              data-testid="toorak-draw-amount"
              className={classes.inputValue}
              disabled={decryptingForEdit}
              style={decryptingForEdit ? { opacity: 0.6 } : undefined}
              onChange={(e: any) =>
                handleEditBankDetails(e.target.value, "bankName")
              }
            />
          ) : null}
          {isSubmitClicked ? showErrorMsg("bankName") : null}
        </Grid>
        <Grid
          item
          container
          xs={fieldWidth}
          className={classes.fieldRow}
          data-testid="account-name-view"
        >
          <DrawViewModeTextField
            label="Account Name"
            value={getDisplayValue("accountName")}
            editedData={maskEncryptedHistoryValues(fieldsHistory.accountName)}
            isEditMode={showingEditBankDetails()}
            enableDecrypt={true}
            canDecrypt={canDecryptHistoryValue}
            onDecrypt={decryptHistoryValue}
            valueEndAdornment={
              !showingEditBankDetails() && canDecryptBankDetails("accountName") ? (
                <IconButton
                  size="small"
                  disabled={loadingField.accountName}
                  onClick={() => toggleReveal("accountName")}
                  data-testid="toggle-accountName-visibility"
                  title={revealed.accountName ? "Hide account name" : "View account name"}
                  style={{ padding: 2 }}
                >
                  {revealed.accountName ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null
            }
          />

          {showingEditBankDetails() ? (
            <input
              value={editDecrypted.accountName ?? bankDetailObj?.accountName ?? ""}
              placeholder={decryptingForEdit ? "Loading..." : "Enter account name"}
              data-testid="toorak-draw-amount"
              className={classes.inputValue}
              disabled={decryptingForEdit}
              style={decryptingForEdit ? { opacity: 0.6 } : undefined}
              onChange={(e: any) =>
                handleEditBankDetails(e.target.value, "accountName")
              }
            />
          ) : null}
          {isSubmitClicked ? showErrorMsg("accountName") : null}
        </Grid>
        <Grid
          item
          container
          xs={12}
          data-testid="bank-address-row"
        >
          <Grid
            item
            container
            xs={fieldWidth}
            className={classes.fieldRow}
            data-testid="city-view"
          >
            <DrawViewModeTextField
              label="Beneficiary City"
              value={bankDetailObj?.city}
              editedData={fieldsHistory.city}
              isEditMode={!!showingEditBankDetails()}
            />

            {showingEditBankDetails() ? (
              <input
                value={bankDetailObj?.city ?? ""}
                placeholder="Enter city"
                data-testid="bank-city-input"
                className={classes.inputValue}
                onChange={(e: any) =>
                  handleEditBankDetails(e.target.value, "city")
                }
              />
            ) : null}
            {isSubmitClicked ? showErrorMsg("city") : null}
          </Grid>
          <Grid
            item
            container
            xs={fieldWidth}
            className={classes.fieldRow}
            data-testid="state-view"
          >
            <DrawViewModeTextField
              label="Beneficiary State"
              value={bankDetailObj?.state}
              editedData={fieldsHistory.state}
              isEditMode={!!showingEditBankDetails()}
            />

            {showingEditBankDetails() ? (
              <StateAutocomplete
                value={bankDetailObj?.state}
                testId="bank-state-select"
                onChange={(code) => handleEditBankDetails(code, "state")}
              />
            ) : null}
            {isSubmitClicked ? showErrorMsg("state") : null}
          </Grid>
          <Grid
            item
            container
            xs={fieldWidth}
            className={classes.fieldRow}
            data-testid="zipcode-view"
          >
            <DrawViewModeTextField
              label="Beneficiary Zip Code"
              value={bankDetailObj?.zipcode}
              editedData={fieldsHistory.zipcode}
              isEditMode={!!showingEditBankDetails()}
            />

            {showingEditBankDetails() ? (
              <input
                value={bankDetailObj?.zipcode ?? ""}
                placeholder="Enter zip code"
                data-testid="bank-zipcode-input"
                className={classes.inputValue}
                inputMode="numeric"
                maxLength={BANK_ZIPCODE_LENGTH}
                onChange={(e: any) =>
                  handleEditBankDetails(e.target.value, "zipcode")
                }
              />
            ) : null}
            {isSubmitClicked ? showErrorMsg("zipcode") : null}
          </Grid>
          <Grid
            item
            container
            xs={fieldWidth}
            className={classes.fieldRow}
            data-testid="country-view"
          >
            <DrawViewModeTextField
              label="Beneficiary Country"
              value={BANK_DEFAULT_COUNTRY}
              editedData={fieldsHistory.country}
              isEditMode={!!showingEditBankDetails()}
            />

            {/* Draws are US-only, so Country is fixed and never editable. */}
            {showingEditBankDetails() ? (
              <input
                value={BANK_DEFAULT_COUNTRY}
                data-testid="bank-country-input"
                className={classes.inputValue}
                readOnly
                disabled
              />
            ) : null}
          </Grid>
        </Grid>
      </Grid>
      <ConfirmFormModal
        isOpen={openFormModal}
        handleClose={(event: any, reason: string) => {
          if (reason == "backdropClick") return;
          setAddBankObj({
            accountNumber: "",
            reAccountNumber: "",
            routing: "",
            reRouting: "",
            bankName: "",
            accountName: "",
            bankDetailId: "",
            ...emptyBankAddress()
          });
          setShowWaringMgs(false);
          setShowRoutingWarningMgs(false);
          setShowZipcodeWarningMgs(false);
          setShowAddAccountNumber(false);
          setShowAddRouting(false);
          setOpenFormModel(false);
        }}
        successHandler={() => {
          updateSelectedBankDetails();
          setOpenFormModel(false);
        }}
        containerStyle={{ overflow: "auto" }}
        header="Add New Bank Account"
        body={bankFormBody()}
        primaryBtnName="Add"
        // disableBackdropClick={true}
        confirmCondition={isDisabled()}
        size="lg"
        headerStyles={{
          padding: 14,
          borderBottom: "solid 1px #dddddd"
        }}
        footerStyles={{
          borderTop: "solid 1px #dddddd",
          padding: "0px 0px 3px 17px",
          margin: "0 0px 14px 0px"
        }}
      />
    </Grid>
  );
};